import React, { useState, useEffect } from 'react'
import { RefreshCw, Download, Factory, CheckCircle2, ShieldAlert, ScanLine, MapPin } from 'lucide-react'
import { supabase } from '../../../lib/supabase'
import { MachineDetailModal } from './MachineDetailModal'

interface Machine {
  id: string
  name: string
  status: 'active' | 'inactive' | 'online' | 'offline' | 'maintenance'
  hal: string
  inspections_today: number
  open_problems: number
  last_inspection: string
}

interface MinimapModuleProps {
  filters: {
    search: string
    showProblems: boolean
    showTasks: boolean
    selectedHal?: string
    selectedStatus?: string
  }
  onHallsLoaded: (halls: string[]) => void
}

export const MinimapModule: React.FC<MinimapModuleProps> = ({ filters, onHallsLoaded }) => {
  const [machines, setMachines] = useState<Machine[]>([])
  const [groupedMachines, setGroupedMachines] = useState<Record<string, Machine[]>>({})
  const [availableHalls, setAvailableHalls] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [refreshCountdown, setRefreshCountdown] = useState(60)
  const [error, setError] = useState<string | null>(null)
  const [inspectionsData, setInspectionsData] = useState<any[]>([])
  const [problemsData, setProblemsData] = useState<any[]>([])
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    loadMachines()
    const interval = setInterval(loadMachines, 60000) // Refresh every minute
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const countdown = setInterval(() => {
      setRefreshCountdown(prev => {
        if (prev <= 1) {
          return 60
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(countdown)
  }, [])

  const loadMachines = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      console.log('🔄 Loading machines and related data from Supabase...')
      
      // Load machines from Supabase with hal field
      // Filter out inactive machines instead of requiring 'active' status
      const { data: machinesData, error: machinesError } = await supabase
        .from('machines')
        .select('id, name, status, hal')
        .neq('status', 'inactive')
        .order('hal, name')

      if (machinesError) {
        console.error('❌ Error loading machines:', machinesError)
        setError('Fout bij het laden van machines')
        return
      }

      console.log('✅ Machines loaded:', machinesData)
      
      // Remove duplicates (inactive machines are already filtered by query)
      const uniqueMachines = machinesData?.filter((machine, index, self) =>
        machine.status !== 'inactive' &&
        self.findIndex(m => m.id === machine.id) === index
      ) || []

      console.log('✅ Filtered unique active machines:', uniqueMachines)

      // Get today's date boundaries (using system/dashboard date)
      const now = new Date()
      const today = now.toISOString().split('T')[0]
      const todayStart = `${today}T00:00:00.000Z`
      const todayEnd = `${today}T23:59:59.999Z`
      
      console.log('📅 Loading data for today:', today, 'from', todayStart, 'to', todayEnd)

      // Load today's inspections only
      const { data: inspections, error: inspectionsError } = await supabase
        .from('quality_control')
        .select('machine_id, created_at')
        .gte('created_at', todayStart)
        .lte('created_at', todayEnd)

      if (inspectionsError) {
        console.warn('⚠️ Error loading today\'s inspections:', inspectionsError)
      } else {
        console.log('✅ Today\'s inspections loaded:', inspections?.length || 0, 'records')
      }

      // Load today's problems only (not just open ones, but all from today)
      const { data: problems, error: problemsError } = await supabase
        .from('probleem_meldingen')
        .select('machine_id, datum_tijd, oplossing_gevonden')
        .gte('datum_tijd', todayStart)
        .lte('datum_tijd', todayEnd)

      if (problemsError) {
        console.warn('⚠️ Error loading today\'s problems:', problemsError)
      } else {
        console.log('✅ Today\'s problems loaded:', problems?.length || 0, 'records')
      }

      console.log('✅ Inspections and problems loaded')

      // Count inspections and problems per machine (only today's data)
      const inspectionCounts: Record<string, number> = {}
      const problemCounts: Record<string, number> = {}

      // Filter and count inspections from today
      inspections?.forEach(inspection => {
        const machineId = inspection.machine_id?.toString()
        const inspectionDate = new Date(inspection.created_at).toISOString().split('T')[0]
        
        if (machineId && inspectionDate === today) {
          inspectionCounts[machineId] = (inspectionCounts[machineId] || 0) + 1
        }
      })

      // Filter and count problems from today (only unsolved ones for status)
      problems?.forEach(problem => {
        const machineId = problem.machine_id?.toString()
        const problemDate = new Date(problem.datum_tijd).toISOString().split('T')[0]
        
        if (machineId && problemDate === today && !problem.oplossing_gevonden) {
          problemCounts[machineId] = (problemCounts[machineId] || 0) + 1
        }
      })

      console.log('📊 Inspection counts for today:', inspectionCounts)
      console.log('📊 Problem counts for today:', problemCounts)

      // Transform data to match our interface
      const transformedMachines: Machine[] = uniqueMachines.map(machine => ({
        id: machine.id?.toString() || machine.name,
        name: machine.name,
        status: machine.status || 'active',
        hal: machine.hal?.trim() || 'Onbekend',
        inspections_today: inspectionCounts[machine.id?.toString()] || 0,
        open_problems: problemCounts[machine.id?.toString()] || 0,
        last_inspection: new Date().toISOString()
      }))

      // Group machines by hall - ensure no duplicate halls
      const grouped = transformedMachines.reduce((acc, machine) => {
        const hall = machine.hal.trim()
        if (!acc[hall]) {
          acc[hall] = []
        }
        acc[hall].push(machine)
        return acc
      }, {} as Record<string, Machine[]>)

      // Get available halls and remove duplicates
      const halls = [...new Set(Object.keys(grouped))].sort()
      
      console.log('✅ Halls found:', halls)
      console.log('✅ Grouped machines:', grouped)

      setMachines(transformedMachines)
      setGroupedMachines(grouped)
      setAvailableHalls(halls)
      setInspectionsData(inspections || [])
      setProblemsData(problems || [])
      
      // Notify parent component about available halls
      onHallsLoaded(halls)

      console.log('✅ Data processing complete. Halls found:', halls)
    } catch (err) {
      console.error('❌ Error loading machines:', err)
      setError('Fout bij het laden van machines')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRefresh = async () => {
    await loadMachines()
    setRefreshCountdown(60)
  }

  const handleExport = () => {
    const csvData = machines.map(machine => ({
      'Machine': machine.name,
      'Hal': machine.hal,
      'Status': machine.status,
      'Inspecties vandaag': machine.inspections_today,
      'Open problemen': machine.open_problems
    }))

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `machines_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const getStatusColor = (machine: Machine) => {
    if (machine.open_problems > 0) return 'bg-orange-500'
    if (machine.inspections_today >= 5) return 'bg-green-500'
    return 'bg-red-500'
  }

  const getStatusText = (machine: Machine) => {
    if (machine.open_problems > 0) return 'Problemen'
    if (machine.inspections_today >= 5) return 'OK'
    return 'Te weinig'
  }

  const handleMachineClick = (machine: Machine) => {
    console.log('🖱️ Machine clicked:', machine.id, 'in hall:', machine.hal)
    setSelectedMachine(machine)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedMachine(null)
  }

  const applyFilters = (machines: Machine[]) => {
    return machines.filter(machine => {
      // Search filter
      if (filters.search && !machine.name.toLowerCase().includes(filters.search.toLowerCase())) {
        return false
      }
      
      // Hall filter
      if (filters.selectedHal && machine.hal !== filters.selectedHal) {
        return false
      }
      
      // Status filter
      if (filters.selectedStatus && machine.status !== filters.selectedStatus) {
        return false
      }
      
      // Problem filter
      if (filters.showProblems && machine.open_problems === 0) {
        return false
      }
      
      // Task filter (machines with too few inspections)
      if (filters.showTasks && machine.inspections_today >= 5) {
        return false
      }
      
      return true
    })
  }

  const filteredMachines = applyFilters(machines)
  
  const filteredGroupedMachines = Object.keys(groupedMachines).reduce((acc, hall) => {
    const filtered = applyFilters(groupedMachines[hall])
    if (filtered.length > 0) {
      acc[hall] = filtered
    }
    return acc
  }, {} as Record<string, Machine[]>)

  return (
    <div className="module-content">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
          <span className="live-dot" /> Live overzicht
          <span style={{ color: 'var(--text-tertiary)' }}>· vernieuwt over {refreshCountdown}s</span>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="btn-secondary flex-1 sm:flex-none !px-3"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Ververs</span>
          </button>
          <button
            onClick={handleExport}
            className="btn-secondary flex-1 sm:flex-none !px-3"
          >
            <Download size={16} />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="legend-bar mb-5">
        <span className="section-label mr-1">Legenda</span>
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
          <div className="flex items-center gap-1">
            <div className="status-dot bg-green-500"></div>
            <span>≥5 inspecties vandaag</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="status-dot bg-red-500"></div>
            <span>&lt;5 inspecties vandaag</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="status-dot bg-orange-500"></div>
            <span>Open problemen</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="text-center text-gray-500 py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-kvh-red"></div>
          <p className="mt-2">Machines laden...</p>
        </div>
      ) : Object.keys(filteredGroupedMachines).length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <p>Geen machines gevonden die voldoen aan de filters.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.keys(filteredGroupedMachines).sort().map(hall => (
            <div key={hall} className="hall-section">
              <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-3">
                  <div className="hall-icon"><Factory size={18} /></div>
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      {hall}
                    </h3>
                    <p className="text-[11px] mt-0.5 flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}><MapPin size={11} /> {filteredGroupedMachines[hall].length} machines op deze locatie</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                  <span className="flex items-center gap-1"><CheckCircle2 size={13} className="text-green-600" /> {filteredGroupedMachines[hall].filter(m => m.inspections_today >= 5).length} op niveau</span>
                  <span className="flex items-center gap-1"><ShieldAlert size={13} className="text-orange-500" /> {filteredGroupedMachines[hall].filter(m => m.open_problems > 0).length} aandacht</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-3">
                {filteredGroupedMachines[hall].map(machine => (
                  <button
                    key={machine.id}
                    type="button"
                    className="machine-card group text-left"
                    onClick={() => handleMachineClick(machine)}
                    title={`${machine.name} (${machine.hal})`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{machine.name}</div>
                        <div className="text-[10px] mt-0.5 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{getStatusText(machine)}</div>
                      </div>
                      <span className={`status-indicator ${getStatusColor(machine)}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <div className="machine-stat">
                        <ScanLine size={13} />
                        <span className="font-semibold">{machine.inspections_today}</span>
                        <span className="hidden 2xl:inline">inspecties</span>
                      </div>
                      <div className={`machine-stat ${machine.open_problems > 0 ? 'machine-stat-alert' : ''}`}>
                        <ShieldAlert size={13} />
                        <span className="font-semibold">{machine.open_problems}</span>
                        <span className="hidden 2xl:inline">problemen</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Machine Detail Modal */}
      {selectedMachine && (
        <MachineDetailModal
          machineId={selectedMachine.id}
          machineName={selectedMachine.name}
          machineHal={selectedMachine.hal}
          isOpen={showModal}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}
